<template>
	<a v-if="url" :href="url" class="Species" :class="classes">
		<span v-if="(commonName && sppPref == 'both') || sppPref == 'common'" class="Species-common">{{ commonName }}</span>
		<span
			v-if="(sciName && sppPref == 'both') || sppPref == 'sci'"
			class="Species-sci"
			:class="sppPref != 'sci' ? 'Species-sub' : ''"
			>{{ sciName }}</span
		>
		<span v-if="credit" class="Species-sub">{{ credit }}</span>
	</a>
	<span v-else class="Species" :class="classes">
		<span v-if="(commonName && sppPref == 'both') || sppPref == 'common'" class="Species-common">{{ commonName }}</span>
		<span
			v-if="(sciName && sppPref == 'both') || sppPref == 'sci'"
			class="Species-sci"
			:class="sppPref != 'sci' ? 'Species-sub' : ''"
			>{{ sciName }}</span
		>
		<span v-if="credit" class="Species-sub">{{ credit }}</span>
	</span>
</template>

<script>
export default {
	data() {
		return {};
	},
	props: {
		/**
		 * Species common name
		 */
		commonName: {
			type: String,
			default: null,
		},
		/**
		 * Species scienctific name
		 */
		sciName: {
			type: String,
			default: null,
		},
		/**
		 * Credit to first described by for scientific contexts
		 */
		credit: {
			type: String,
			default: null,
		},
		/**
		 * eBird user preference for species name display. Options: "common", "sci", or "both"
		 * @values common, sci, both
		 */
		sppPref: {
			type: String,
			default: 'both',
			validator: function (value) {
				// The value must match one of these strings
				return ['common', 'sci', 'both'].indexOf(value) !== -1;
			},
		},
		/**
		 * Non-species taxa get a slightly different treatment
		 */
		nonSpeciesTaxa: {
			type: Boolean,
			default: false,
		},
		/**
		 * Optional URL to make into a link
		 */
		url: {
			type: String,
			default: null,
		},
		/**
		 * For dark backgrounds
		 */
		reverse: {
			type: Boolean,
			default: false,
		},
		/**
		 * Stack vertically
		 */
		stacked: {
			type: Boolean,
			default: false,
		},
		/**
		 * Mimic heading sizes without making into a real heading. Options: 1-6
		 */
		headingStyle: {
			type: Number,
			default: null,
		},
	},
	computed: {
		classes() {
			const classes = {
				'Species--reverse': this.reverse,
				'Species--stacked': this.stacked,
				'Species--nonSpeciesTaxa': this.nonSpeciesTaxa,
			};
			if (this.headingStyle) {
				classes['Species--h' + this.headingStyle] = true;
			}
			return classes;
		},
	},
};
</script>

<style lang="scss">
.Species {
	color: $color-text;
	font-weight: bold;
	display: inline-flex;
	flex-wrap: wrap;
	align-items: baseline;
	span:not(:last-child) {
		margin-right: $space-rel-sm;
		[dir='rtl'] & {
			margin-right: 0;
			margin-left: $space-rel-sm;
		}
	}
}
a.Species {
	@include link;
}
p > .Species {
	display: inline;
	.Species-sub {
		font-size: 100%;
	}
	span:not(:last-child) {
		margin: 0 !important;
		&::after {
			content: ' ';
		}
	}
}

.Species-sub {
	color: $color-text-muted;
	font-weight: normal;
	font-size: 90%;
}

.Species-sci {
	font-style: italic;
}

// Variants
.Species.Species--reverse {
	color: $color-text-reverse;
	.Species-sub,
	a .Species-sub {
		color: $color-text-muted-reverse;
	}
}
a.Species.Species--reverse {
	@include link($color-link-reverse, $reverse: true);
}
.Species.Species--stacked {
	flex-direction: column;
	line-height: 1.25;
	span:not(:last-child) {
		margin-right: 0;
		margin-bottom: $space-rel-xs;
		[dir='rtl'] & {
			margin-left: 0;
		}
	}
}
.Species.Species--nonSpeciesTaxa {
	font-weight: normal;
	.Species-common,
	.Species-sci {
		font-weight: normal;
	}
	&:not(a) {
		color: $color-text;
	}
	&.Species--reverse:not(a) {
		color: $color-text-reverse;
	}
}

// Heading variants
h1 > .Species,
.Heading--h1 > .Species,
.Species.Species--h1 {
	@include h1;
	.Species-sub {
		font-size: 75%;
		letter-spacing: 0;
	}
}
h2 > .Species,
.Heading--h2 > .Species,
.Species.Species--h2 {
	@include h2;
	.Species-sub {
		font-size: 75%;
		letter-spacing: 0;
	}
}
h3 > .Species,
.Heading--h3 > .Species,
.Species.Species--h3 {
	@include h3;
	.Species-sub {
		font-size: 85%;
		letter-spacing: 0;
	}
}
h4 > .Species,
.Heading--h4 > .Species,
.Species.Species--h4 {
	@include h4;
	.Species-sub {
		font-size: 85%;
		letter-spacing: 0;
	}
}
h5 > .Species,
.Heading--h5 > .Species,
.Species.Species--h5 {
	@include h5;
	.Species-sub {
		font-size: 95%;
		letter-spacing: 0;
	}
}
h6 > .Species,
.Heading--h6 > .Species,
.Species.Species--h6 {
	@include h6;
	.Species-sub {
		font-size: 95%;
		letter-spacing: 0;
	}
}

// Remove redundant margin bottom when inside Heading
h1 > .Species,
h2 > .Species,
h3 > .Species,
h4 > .Species,
h5 > .Species,
h6 > .Species,
.Heading > .Species {
	margin-bottom: 0;
}

// Thin heading variants
.Heading--h1.Heading--thin > .Species {
	@include h1--thin;
	.Species-sub {
		@include h1--thin;
	}
}
.Heading--h2.Heading--thin > .Species {
	@include h2--thin;
	.Species-sub {
		@include h2--thin;
	}
}
</style>
